/**
 * Заголовок вкладки и мета-теги страницы.
 *
 * Сервер на любой адрес отдаёт один и тот же index.html, и без этого хука у
 * каталога, квеста и лидерборда были бы одинаковые заголовок и описание —
 * поисковик склеивает такие страницы в одну. Google выполняет JS и видит то,
 * что проставлено здесь; роботы без JS получают значения из index.html.
 */
import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const SITE = "ChronoGuessr";
const ORIGIN = "https://chronoguessr.io";

/** Без миниатюры в выдаче — см. комментарий у тега robots в index.html. */
const ROBOTS = "max-image-preview:none";

// Значения из index.html: к ним страница возвращается, когда своих не задала.
const defaults = {
  title: document.title,
  description: metaContent("name", "description"),
};

interface PageMeta {
  /** Название страницы без имени сайта; пусто — заголовок главной. */
  title?: string;
  /** Текст сниппета; пусто — общее описание игры. */
  description?: string;
  /** Страницы, которым нечего делать в выдаче: 404, личные, служебные. */
  noindex?: boolean;
}

export function usePageMeta({ title, description, noindex = false }: PageMeta) {
  const { pathname } = useLocation();

  useEffect(() => {
    const fullTitle = title ? `${title} — ${SITE}` : defaults.title;
    const text = clip(description || defaults.description);
    const url = ORIGIN + pathname;

    document.title = fullTitle;
    setMeta("name", "description", text);
    setMeta("property", "og:title", fullTitle);
    setMeta("property", "og:description", text);
    setMeta("property", "og:url", url);
    setMeta("name", "robots", noindex ? `noindex, ${ROBOTS}` : ROBOTS);
    setCanonical(url);
  }, [title, description, noindex, pathname]);
}

/** Сниппет длиннее ~160 символов поисковик обрежет сам, но посреди слова. */
function clip(text: string, max = 160): string {
  const flat = text.replace(/\s+/g, " ").trim();
  if (flat.length <= max) return flat;
  const cut = flat.slice(0, max - 1);
  return cut.slice(0, cut.lastIndexOf(" ")).replace(/[,.;:—-]$/, "") + "…";
}

function metaContent(attr: "name" | "property", key: string): string {
  return document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`)?.content ?? "";
}

/** null убирает тег. */
function setMeta(attr: "name" | "property", key: string, value: string | null) {
  let tag = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (value === null) {
    tag?.remove();
    return;
  }
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute(attr, key);
    document.head.appendChild(tag);
  }
  tag.content = value;
}

function setCanonical(url: string) {
  let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!link) {
    link = document.createElement("link");
    link.rel = "canonical";
    document.head.appendChild(link);
  }
  link.href = url;
}
